import React, { useEffect, useState } from 'react'

import { Stack, Typography } from '@mui/material'

import { Modal, Button, PhoneField, FieldLabel } from '@repo/ui'
import { CodeInput } from 'components/input'
import { SECURITY_SMS_CONSENT_TEXT } from 'constants/smsConsent'
import { useSnackbar } from 'contexts'
import { getErrorMessage } from 'utils'

import {
  useConfirmSmsEnrollmentMutation,
  useStartSmsEnrollmentMutation,
} from 'hooks/api/useAuthFactors'

interface EnrollSmsModalProps {
  open: boolean
  onClose: () => void
  onConfirmed: (recoveryCodes?: string[]) => void
}

// VRG-490: 6, matching what StartSmsEnrollmentUseCase now sends AND the
// login challenge screen. Was 4 while enrollment fell through to OtpEntity's
// 4-digit Math.random() default.
const CODE_LENGTH = 6

type Step = 'phone' | 'code'

/**
 * VRG-490 — two steps in one modal (phone → code), rather than two modals,
 * matching the app's own `PhoneVerificationDrawer` shape
 * (working-conventions §52's "step machine, not a launchpad" — same
 * principle applies to a modal as to a bottom sheet).
 */
export const EnrollSmsModal = ({ open, onClose, onConfirmed }: EnrollSmsModalProps) => {
  const { open: notification } = useSnackbar()
  const [step, setStep] = useState<Step>('phone')
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [reference, setReference] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const { startSmsEnrollmentAsync, isStartingSmsEnrollment } = useStartSmsEnrollmentMutation()
  const { confirmSmsEnrollmentAsync, isConfirmingSmsEnrollment } = useConfirmSmsEnrollmentMutation()

  useEffect(() => {
    if (open) {
      setStep('phone')
      setPhone('')
      setCode('')
      setReference(null)
      setError(null)
    }
  }, [open])

  const handleSendCode = async () => {
    if (!phone.trim() || isStartingSmsEnrollment) return
    setError(null)
    try {
      const response = await startSmsEnrollmentAsync({ phone: phone.trim() })
      setReference(response.reference)
      setCode('')
      setStep('code')
    } catch (err) {
      setError(getErrorMessage(err))
    }
  }

  // `submitted` comes from CodeInput's `onComplete`, which fires inside the same
  // event that sets the code — state is one render behind. The button path passes
  // nothing and reads state as before.
  const handleConfirm = async (submitted?: string) => {
    const value = (submitted ?? code).trim()
    if (!reference || value.length !== CODE_LENGTH || isConfirmingSmsEnrollment) return
    setError(null)
    try {
      const response = await confirmSmsEnrollmentAsync({ reference, code: value })
      notification({ type: 'success', title: 'SMS method added', text: 'You can now use it to sign in.' })
      onConfirmed(response.recoveryCodes)
    } catch (err) {
      setError(getErrorMessage(err))
    }
  }

  return (
    <Modal
      title={step === 'phone' ? 'Add phone number' : 'Enter verification code'}
      open={open}
      onClose={onClose}
      actions={
        step === 'phone' ? (
          <>
            <Button disableElevation variant="secondary" onClick={onClose} disabled={isStartingSmsEnrollment}>
              Cancel
            </Button>
            <Button
              disableElevation
              variant="primary"
              loading={isStartingSmsEnrollment}
              disabled={!phone.trim() || isStartingSmsEnrollment}
              onClick={handleSendCode}
            >
              Send code
            </Button>
          </>
        ) : (
          <>
            <Button disableElevation variant="secondary" onClick={() => setStep('phone')} disabled={isConfirmingSmsEnrollment}>
              Back
            </Button>
            <Button
              disableElevation
              variant="primary"
              loading={isConfirmingSmsEnrollment}
              disabled={code.trim().length !== CODE_LENGTH || isConfirmingSmsEnrollment}
              // Wrapped — passing by reference would submit the MouseEvent as the code.
              onClick={() => handleConfirm()}
            >
              Confirm
            </Button>
          </>
        )
      }
    >
      {step === 'phone' ? (
        <Stack spacing={4}>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            We'll text you a one-time code when you sign in or confirm a security change.
          </Typography>
          <Stack spacing={1.5}>
            <PhoneField
              label="Phone number"
              value={phone}
              onChange={value => {
                setPhone(value)
                setError(null)
              }}
              error={Boolean(error)}
              helperText={error ?? undefined}
              disabled={isStartingSmsEnrollment}
              autoFocus
            />
            {/* Carrier evidence: quoted verbatim on apps/app/public/sms.html. Always visible,
                never replaced by an error. Wording lives in constants/smsConsent.ts. */}
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
              {SECURITY_SMS_CONSENT_TEXT}
            </Typography>
          </Stack>
        </Stack>
      ) : (
        <Stack spacing={4}>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            Enter the {CODE_LENGTH}-digit code we sent to {phone}.
          </Typography>
          {/* VRG-610 — same per-digit boxes as the sign-in challenge and the
              authenticator modal. All four code entries in the platform now look
              and behave identically. */}
          <FieldLabel label="Verification code" />
          <CodeInput
            autoFocus
            value={code}
            onChange={value => {
              setCode(value)
              setError(null)
            }}
            onComplete={value => handleConfirm(value)}
            disabled={isConfirmingSmsEnrollment}
            length={CODE_LENGTH}
            aria-label="Verification code"
          />
          {error && (
            <Typography variant="body3" color="error.main">
              {error}
            </Typography>
          )}
          <Button disableElevation variant="tertiary" onClick={handleSendCode} disabled={isStartingSmsEnrollment}>
            Resend code
          </Button>
        </Stack>
      )}
    </Modal>
  )
}
